// Add this new component file: CreatorDeduplication.jsx

import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import client from '../../../../../API';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  MergeType as MergeIcon
} from '@mui/icons-material';
import { toast } from 'sonner';

const CreatorDeduplication = () => {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [targetCreatorId, setTargetCreatorId] = useState('');
  const [sourceCreatorIds, setSourceCreatorIds] = useState([]);
  const [mergeReason, setMergeReason] = useState('');
  const [previewData, setPreviewData] = useState(null);

  // Query to detect duplicates
  const {
    data: duplicateData,
    isLoading: duplicatesLoading,
    error: duplicatesError,
    refetch: refetchDuplicates
  } = useQuery({
    queryKey: ['creator-duplicates'],
    queryFn: () => client.creators.detectDuplicates(),
    refetchOnWindowFocus: false
  });

  // Mutation to merge creators
  const { mutate: mergeCreators, isLoading: mergeLoading } = useMutation(
    (mergeData) => client.creators.mergeCreators(mergeData),
    {
      onSuccess: (result) => {
        toast.success('Creators merged successfully', {
          description: `Merged ${result.merge_summary.merged_creators.length} creators into ${result.merge_summary.target_creator}`
        });
        setMergeDialogOpen(false);
        setSelectedGroup(null);
        setTargetCreatorId('');
        setSourceCreatorIds([]);
        setMergeReason('');
        setPreviewData(null);
        refetchDuplicates();
      },
      onError: (error) => {
        toast.error('Merge failed', {
          description: error.response?.data?.error || error.message
        });
      }
    }
  );

  // Mutation to get merge preview
  const { mutate: getPreview, isLoading: previewLoading } = useMutation(
    (creatorId) => client.creators.previewMerge(creatorId),
    {
      onSuccess: (data) => {
        setPreviewData(data);
      },
      onError: (error) => {
        toast.error('Preview failed', {
          description: error.response?.data?.error || error.message
        });
      }
    }
  );

  const handleOpenMergeDialog = (group) => {
    setSelectedGroup(group);
    setMergeDialogOpen(true);
    setMergeReason('Admin deduplication - duplicate contact information');
    
    // Auto-select recommended target (creator with user account or most payouts)
    const recommendedTarget = group.creators.find(c => c.has_user_account) || 
                             group.creators.reduce((prev, current) => 
                               (prev.payout_count > current.payout_count) ? prev : current
                             );
    
    if (recommendedTarget) {
      setTargetCreatorId(recommendedTarget.creator);
      setSourceCreatorIds(group.creators
        .filter(c => c.creator !== recommendedTarget.creator)
        .map(c => c.creator)
      );
      getPreview(recommendedTarget.creator);
    }
  };

  const handleTargetChange = (event) => {
    const newTarget = event.target.value;
    setTargetCreatorId(newTarget);
    
    if (selectedGroup) {
      setSourceCreatorIds(selectedGroup.creators
        .filter(c => c.creator !== newTarget)
        .map(c => c.creator)
      );
      getPreview(newTarget);
    }
  };

  const handleMerge = () => {
    if (!targetCreatorId || sourceCreatorIds.length === 0) {
      toast.error('Please select target and source creators');
      return;
    }

    mergeCreators({
      target_creator_id: targetCreatorId,
      source_creator_ids: sourceCreatorIds,
      reason: mergeReason
    });
  };

  const renderCreatorSummary = (creator) => (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">{creator.display_name || creator.creator}</Typography>
          {creator.has_user_account && (
            <Chip label="Has Account" color="success" size="small" />
          )}
          {creator.payout_count > 0 && (
            <Chip label={`${creator.payout_count} Payouts`} color="info" size="small" />
          )}
        </Box>
        
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {creator.email || creator.payout_email || 'No email'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {creator.phone_number || 'No phone'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Status: {creator.status || 'None'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Last Payout: {creator.latest_payout_date || 'Never'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (duplicatesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (duplicatesError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load duplicates: {duplicatesError.message}
      </Alert>
    );
  }

  const { duplicate_groups = [], total_groups = 0, total_creators_affected = 0 } = duplicateData || {};

  return (
    <Box sx={{ p: 2 }}>
      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                Duplicate Groups Found
              </Typography>
              <Typography variant="h4">{total_groups}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                Creators Affected
              </Typography>
              <Typography variant="h4">{total_creators_affected}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Button
                variant="contained"
                fullWidth
                onClick={refetchDuplicates}
                disabled={duplicatesLoading}
              >
                Refresh Detection
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Duplicate Groups */}
      {duplicate_groups.length === 0 ? (
        <Alert severity="success">
          No duplicate creators found! All creators have unique contact information.
        </Alert>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Duplicate Groups ({duplicate_groups.length})
          </Typography>
          
          {duplicate_groups.map((group, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <WarningIcon color="warning" />
                  <Box>
                    <Typography variant="subtitle1">
                      {group.match_type === 'phone' ? 'Phone' : 'Email'} Match: {group.match_value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.creator_count} creators affected
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto' }}>
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<MergeIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenMergeDialog(group);
                      }}
                    >
                      Merge Creators
                    </Button>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {group.creators.map((creator, creatorIndex) => (
                    <Grid item xs={12} md={6} key={creatorIndex}>
                      {renderCreatorSummary(creator)}
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Merge Dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MergeIcon />
            Merge Duplicate Creators
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedGroup && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action will merge multiple creator records into one. All payouts, 
                withdrawals, and transactions will be moved to the target creator. 
                Source creators will be marked as deleted but preserved for audit.
              </Alert>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Target Creator (Keep)</InputLabel>
                <Select
                  value={targetCreatorId}
                  onChange={handleTargetChange}
                  label="Target Creator (Keep)"
                >
                  {selectedGroup.creators.map((creator) => (
                    <MenuItem key={creator.creator} value={creator.creator}>
                      <Box>
                        <Typography>
                          {creator.display_name || creator.creator}
                          {creator.has_user_account && ' (Has Account)'}
                          {creator.payout_count > 0 && ` (${creator.payout_count} payouts)`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {creator.email || creator.payout_email || 'No email'} • 
                          {creator.phone_number || 'No phone'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Merge Reason"
                value={mergeReason}
                onChange={(e) => setMergeReason(e.target.value)}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />

              {previewData && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Merge Preview</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Target: {previewData.creator.creator_id} 
                      {previewData.has_user_account && ' (Verified Account)'}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Payouts: {previewData.related_records.payouts}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Withdrawals: {previewData.related_records.withdrawals}
                        </Typography>
                      </Grid>
                    </Grid>

                    {previewData.recommended_as_target && (
                      <Chip label="Recommended Target" color="success" size="small" sx={{ mt: 1 }} />
                    )}
                  </CardContent>
                </Card>
              )}

              {sourceCreatorIds.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Creators to be merged (will be marked as deleted):
                  </Typography>
                  <List dense>
                    {sourceCreatorIds.map((id) => {
                      const creator = selectedGroup.creators.find(c => c.creator === id);
                      return (
                        <ListItem key={id}>
                          <ListItemText
                            primary={creator?.display_name || id}
                            secondary={`${creator?.payout_count || 0} payouts`}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleMerge}
            variant="contained"
            color="warning"
            disabled={!targetCreatorId || sourceCreatorIds.length === 0 || mergeLoading}
          >
            {mergeLoading ? <CircularProgress size={20} /> : 'Merge Creators'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatorDeduplication;